import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import IconMC from '../../../components/IconMC';
import SimpleAccordionMC from '../../../components/SimpleAccordionMC';
import { formatDate } from '../../../../util/utils';
import useLeadScoreHistoryData from '../../../hooks/inbox/data/useLeadScoreHistoryData';
import {
  TEMPERATURE_LEVELS, TEMPERATURE_ICONS, getScoreColor, getScoreBarColor, getTemperatureKey, getTemperatureLevelFromScore
} from '../utils/leadScoreUtils';

const formatShortDelta = (msDiff) => {
  const seconds = Math.round(msDiff / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

const LeadScoreTimelineEntry = ({ entry, previousCreatedAt, retailerInfo }) => {
  const { t, i18n } = useTranslation('InboxMC');
  const level = getTemperatureLevelFromScore(entry.score ?? 0);
  const temperatureKey = getTemperatureKey(level);
  const TemperatureIcon = TEMPERATURE_ICONS[level];
  const delta = previousCreatedAt
    ? formatShortDelta(new Date(previousCreatedAt) - new Date(entry.created_at))
    : null;

  return (
    <>
      {delta && (
        <div className="tw-relative tw-flex tw-items-start tw-gap-2">
          <div className="tw-flex-shrink-0 tw-w-6 tw-flex tw-justify-center">
            <span className="tw-relative tw-z-10 tw-bg-white tw-text-[10px] tw-text-gray-400">{delta}</span>
          </div>
        </div>
      )}
      <div className="tw-relative tw-flex tw-items-start tw-gap-2">
        <div className="tw-relative tw-flex-shrink-0 tw-w-6 tw-flex tw-justify-center tw-pt-0.5">
          <span className="tw-relative tw-z-10 tw-bg-white tw-rounded-full tw-ring-2 tw-ring-white">
            <TemperatureIcon size={12} className={getScoreColor(entry.score ?? 0)} />
          </span>
        </div>
        <div className="tw-min-w-0 tw-flex-1 tw-pb-3">
          <div className="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
            <span className={`tw-text-[10px] tw-font-semibold ${getScoreColor(entry.score ?? 0)}`}>
              {`${temperatureKey ? t(temperatureKey) : ''} · ${entry.score}`}
            </span>
            <span className="tw-text-[10px] tw-text-gray-400">
              {`- ${formatDate(retailerInfo, entry.created_at, 'ddd, MMM D, h:mm A', i18n.language)}`}
            </span>
          </div>
          {entry.summary && (
            <p className="tw-text-[11px] tw-text-gray-500 tw-leading-relaxed tw-mt-0.5">
              {entry.summary}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

const LeadScoreTimeline = ({ customerId, platform, retailerInfo }) => {
  const { t } = useTranslation('InboxMC');
  const {
    history, loading, hasMore, loadMore, error, retry
  } = useLeadScoreHistoryData({ customerId, platform });

  const isInitialLoad = loading && history.length === 0 && !error;

  if (!loading && !error && history.length === 0) return null;

  return (
    <>
      <div className="tw-border-t tw-border-gray-200" />
      <div>
        <span className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider">
          {t('copilot_ventas.timeline_title')}
        </span>
        {isInitialLoad ? (
          <p className="tw-text-[10px] tw-text-gray-400 tw-mt-2">{t('copilot_ventas.timeline_loading')}</p>
        ) : (
          <>
            <div className="tw-relative tw-mt-2">
              <div className="tw-absolute tw-left-[11px] tw-top-2 tw-bottom-2 tw-w-px tw-bg-gray-200" />
              {history.map((entry, idx) => (
                <LeadScoreTimelineEntry
                  key={entry.id || entry.message_id}
                  entry={entry}
                  previousCreatedAt={idx > 0 ? history[idx - 1].created_at : null}
                  retailerInfo={retailerInfo}
                />
              ))}
            </div>
            {error && (
              <p className="tw-text-[10px] tw-text-red-500 tw-mt-2">
                {`${t('copilot_ventas.timeline_error')} `}
                <button type="button" onClick={retry} className="tw-underline tw-font-medium">
                  {t('copilot_ventas.timeline_retry')}
                </button>
              </p>
            )}
            {!error && hasMore && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="tw-text-[10px] tw-text-blue-500 tw-font-medium tw-mt-2 tw-disabled:tw-opacity-50"
              >
                {loading ? t('copilot_ventas.timeline_loading') : t('copilot_ventas.timeline_load_more')}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};

const LeadScoreDetailMC = memo(({
  leadScore, loading, customerId, platform
}) => {
  const { t, i18n } = useTranslation('InboxMC');
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);

  const scoreValue = useMemo(() => leadScore?.lead_score ?? 0, [leadScore?.lead_score]);

  const temperatureLevel = useMemo(() => getTemperatureLevelFromScore(scoreValue), [scoreValue]);

  const temperatureLabel = useMemo(() => {
    const key = getTemperatureKey(temperatureLevel);
    return key ? t(key) : null;
  }, [temperatureLevel, t]);

  const TemperatureIcon = TEMPERATURE_ICONS[temperatureLevel];

  if (loading && leadScore === null) {
    return (
      <div className="tw-wrapper tw-p-3 tw-space-y-3 tw-animate-pulse">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-h-3 tw-w-20 tw-bg-gray-200 tw-rounded" />
          <div className="tw-h-6 tw-w-16 tw-bg-gray-200 tw-rounded" />
        </div>
        <div className="tw-h-2 tw-w-full tw-bg-gray-200 tw-rounded-full" />
        <div className="tw-h-3 tw-w-24 tw-bg-gray-200 tw-rounded" />
        <div className="tw-h-3 tw-w-full tw-bg-gray-200 tw-rounded" />
      </div>
    );
  }

  if (!loading && leadScore === null) {
    return (
      <div className="tw-wrapper tw-p-3 tw-text-center tw-text-xs tw-text-gray-400">
        {t('copilot_ventas.no_score')}
      </div>
    );
  }

  return (
    <div className="tw-wrapper tw-p-1 tw-space-y-3">
      <div>
        <span className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider">
          {t('copilot_ventas.score_label')}
        </span>
        <div className="tw-flex tw-items-baseline tw-gap-0.5">
          <span className="tw-text-2xl tw-font-bold tw-text-gray-900">
            {scoreValue}
          </span>
          <span className="tw-text-sm tw-text-gray-400">/100</span>
        </div>
      </div>

      <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2">
        <div
          className={`tw-h-2 tw-rounded-full tw-transition-all tw-duration-300 ${getScoreBarColor(scoreValue)}`}
          style={{ width: `${scoreValue}%` }}
        />
      </div>

      {temperatureLabel && (
        <div className="tw-flex tw-items-center tw-gap-1">
          <TemperatureIcon size={16} className={getScoreColor(scoreValue)} />
          <span className={`tw-text-xs tw-font-semibold ${getScoreColor(scoreValue)}`}>
            {temperatureLabel}
          </span>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-between">
        {TEMPERATURE_LEVELS.map((level) => (
          <span
            key={level}
            className={`tw-text-[10px] ${level === temperatureLevel ? `tw-font-semibold ${getScoreColor(scoreValue)}` : 'tw-text-gray-400'}`}
          >
            {t(getTemperatureKey(level))}
          </span>
        ))}
      </div>

      {leadScore?.attention_reason && (
        <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed">
          {leadScore.attention_reason}
        </p>
      )}

      {leadScore?.updated_at && (
        <>
          <div className="tw-border-t tw-border-gray-200" />
          <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-400">
            <IconMC name="clock-outline" size="xs" />
            <span className="tw-text-[10px]">
              {t('copilot_ventas.updated_at', {
                date: formatDate(retailer_info, leadScore.updated_at, 'DD MMM YYYY', i18n.language),
                time: formatDate(retailer_info, leadScore.updated_at, 'h:mm A', i18n.language)
              })}
            </span>
          </div>
        </>
      )}

      {leadScore?.summary && (
        <div>
          <span className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider">
            {t('copilot_ventas.summary')}
          </span>
          <p className="tw-text-xs tw-text-gray-700 tw-leading-relaxed tw-mt-1">
            {leadScore.summary}
          </p>
        </div>
      )}

      {leadScore?.conversation_resume && (
        <SimpleAccordionMC
          key={customerId}
          label={t('copilot_ventas.conversation_resume_title')}
          labelClassName="tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider"
          className="!tw-py-0"
          childrenClassName="tw-mt-1"
          renderCloseComponent={() => (
            <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed tw-mt-1 tw-line-clamp-2">
              {leadScore.conversation_resume}
            </p>
          )}
        >
          <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed">
            {leadScore.conversation_resume}
          </p>
        </SimpleAccordionMC>
      )}

      <LeadScoreTimeline customerId={customerId} platform={platform} retailerInfo={retailer_info} />
    </div>
  );
});

LeadScoreDetailMC.displayName = 'LeadScoreDetailMC';

export default LeadScoreDetailMC;
